import Chart from "../components/Chart";
import DataTable from "../components/DataTable";
import HTMLStrip from "../components/HTMLStrip";
import KPITile from "../components/KPITile";
import UpgradeUnlock from "../components/UpgradeUnlock";
import WidgetRenderer from "../components/WidgetRenderer";
import EmptyState from "../components/states/EmptyState";
import ErrorState from "../components/states/ErrorState";
import Skeleton from "../components/states/Skeleton";

import type { ManifestWidget } from "../lib/manifest";

/**
 * MER-114 Phase 4 acceptance: Storybook-equivalent visual review of
 * every widget type. Renders one of each with realistic mock data so
 * cowork + AJ can sanity-check styling and behavior without needing
 * Supabase populated. Every entry uses `WidgetRenderer` with `mockData`
 * so the component graph that Production will exercise is the same one
 * showing here.
 */

const KPI_WIDGET: ManifestWidget = {
  id: "demo.kpi",
  name: "Total Revenue (MTD)",
  tab: "demo",
  type: "kpi",
  kpi_subtype: "single_number",
  source: { kind: "view", name: "view_cqt_01" },
  field: { column: "bill_amount_gross", aggregation: "sum" },
  display_format: "currency_usd",
  what_it_shows: "Earned revenue this month so far.",
};

const BAR_WIDGET: ManifestWidget = {
  id: "demo.bar",
  name: "Revenue by Division",
  tab: "demo",
  chart_type: "bar",
  source: { kind: "view", name: "view_cqt_01" },
  field: { column: "Earned_Revenue", aggregation: "sum" },
};

const STACKED_WIDGET: ManifestWidget = {
  ...BAR_WIDGET,
  id: "demo.stacked",
  name: "Stacked",
  chart_type: "stacked_bar",
};
const LINE_WIDGET: ManifestWidget = {
  ...BAR_WIDGET,
  id: "demo.line",
  name: "Revenue Trend",
  chart_type: "line",
};
const TABULAR_WIDGET: ManifestWidget = {
  id: "demo.table",
  name: "Top AR Accounts",
  tab: "demo",
  chart_type: "tabular",
  source: { kind: "table", name: "top_ar_accounts" },
  field: { column: "total_balance" },
};

const PIVOT_WIDGET: ManifestWidget = {
  id: "demo.pivot",
  name: "AR Aging by Division",
  tab: "demo",
  chart_type: "pivot",
  source: { kind: "view", name: "view_cqt_40" },
  field: { column: "amount" },
};

const HTML_WIDGET: ManifestWidget = {
  id: "demo.html",
  name: "About",
  tab: "demo",
  chart_type: "html_strip",
  what_it_shows: "Phase 4 demo: <em>Storybook-equivalent</em> gallery for every widget type.",
};

const MOCK_KPI = { kind: "kpi" as const, scalar: 1234567 };
const MOCK_KPI_PCT = { kind: "kpi" as const, scalar: 0.872 };
const MOCK_BAR_ROWS = {
  kind: "rows" as const,
  rows: [
    { division: "Jackson", Earned_Revenue: 826303 },
    { division: "Hattiesburg", Earned_Revenue: 265724 },
    { division: "Dallas", Earned_Revenue: 72262 },
  ],
};
const MOCK_LINE_ROWS = {
  kind: "rows" as const,
  rows: [
    { period: "2026-01", Earned_Revenue: 759403 },
    { period: "2026-02", Earned_Revenue: 593711 },
    { period: "2026-03", Earned_Revenue: 1164290 },
    { period: "2026-04", Earned_Revenue: 1043637 },
  ],
};
const MOCK_TABLE_ROWS = {
  kind: "rows" as const,
  rows: [
    { account: "Acme Corp", division: "Jackson", total_balance: 12450.0 },
    { account: "Walmart Logistics", division: "Hattiesburg", total_balance: 9874.5 },
    { account: "City of Dallas", division: "Dallas", total_balance: 6320.0 },
  ],
};
const MOCK_PIVOT_ROWS = {
  kind: "rows" as const,
  rows: [
    { division: "Jackson", bucket: "Current", amount: 245000 },
    { division: "Jackson", bucket: "30-60", amount: 82000 },
    { division: "Jackson", bucket: "60+", amount: 31000 },
    { division: "Hattiesburg", bucket: "Current", amount: 188000 },
    { division: "Hattiesburg", bucket: "30-60", amount: 41000 },
    { division: "Hattiesburg", bucket: "60+", amount: 12000 },
  ],
};

interface CellProps {
  title: string;
  children: React.ReactNode;
}

function Cell(props: CellProps) {
  return (
    <section
      style={{
        display: "grid",
        gap: 8,
        padding: 16,
        background: "var(--bg-surface)",
        borderRadius: 14,
      }}
    >
      <h2
        style={{
          fontSize: 13,
          color: "var(--text-muted)",
          textTransform: "uppercase",
          letterSpacing: 0.5,
        }}
      >
        {props.title}
      </h2>
      {props.children}
    </section>
  );
}

export default function WidgetGallery() {
  return (
    <main
      style={{
        padding: "24px 32px",
        display: "grid",
        gap: 16,
        gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
      }}
    >
      <header style={{ gridColumn: "1 / -1" }}>
        <h1 style={{ margin: 0 }}>Widget Gallery</h1>
        <p style={{ color: "var(--text-muted)", marginTop: 4 }}>
          MER-114 Phase 4. One of every widget type using `WidgetRenderer` with mock data.
        </p>
      </header>

      <Cell title="single_number (KPI)">
        <WidgetRenderer widget={KPI_WIDGET} mockData={MOCK_KPI} />
      </Cell>
      <Cell title="single_number (snapshot, percent format)">
        <KPITile title="Collection Rate" value={87.2} format="decimal_1" caption="Snapshot KPI" />
      </Cell>
      <Cell title="ratio format">
        <KPITile title="Margin (ratio)" value={MOCK_KPI_PCT.scalar} format="ratio" />
      </Cell>

      <Cell title="bar">
        <WidgetRenderer widget={BAR_WIDGET} mockData={MOCK_BAR_ROWS} />
      </Cell>
      <Cell title="stacked_bar">
        <WidgetRenderer widget={STACKED_WIDGET} mockData={MOCK_BAR_ROWS} />
      </Cell>
      <Cell title="line">
        <WidgetRenderer widget={LINE_WIDGET} mockData={MOCK_LINE_ROWS} />
      </Cell>

      <Cell title="tabular">
        <WidgetRenderer widget={TABULAR_WIDGET} mockData={MOCK_TABLE_ROWS} />
      </Cell>
      <Cell title="pivot">
        <WidgetRenderer widget={PIVOT_WIDGET} mockData={MOCK_PIVOT_ROWS} />
      </Cell>
      <Cell title="html_strip">
        <WidgetRenderer widget={HTML_WIDGET} />
      </Cell>

      <Cell title="UpgradeUnlock (Growth tier on Pro tab)">
        <UpgradeUnlock tabLabel="Profitability" requiredTier="professional" />
      </Cell>
      <Cell title="Skeleton (loading)">
        <Skeleton label="Total Revenue (MTD)" />
      </Cell>
      <Cell title="ErrorState">
        <ErrorState message="Query failed: view_cqt_99 does not exist" />
      </Cell>
      <Cell title="EmptyState">
        <EmptyState title="No data for this period" caption="Apr 2026" />
      </Cell>

      <Cell title="HTMLStrip standalone">
        <HTMLStrip html="<strong>Disposal fees</strong> are pass-through to the customer when contract terms allow it." />
      </Cell>
      <Cell title="DataTable standalone">
        <DataTable
          columns={["account", "division", "balance"]}
          rows={[
            ["Acme", "Jackson", 12450],
            ["Walmart", "HBG", 9874],
          ]}
        />
      </Cell>
      <Cell title="Chart standalone (pie)">
        <Chart
          type="pie"
          data={{
            labels: ["Front Load", "Roll Off", "Side Load"],
            datasets: [
              {
                data: [612, 686, 325],
                backgroundColor: ["#5277b2", "#7aa3e0", "#bcd3f0"],
              },
            ],
          }}
        />
      </Cell>
    </main>
  );
}
